import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ReactComponent as XCloseIcon } from "../../../../assets/icons/x-close1.svg";
import { ReactComponent as CheckArrowIcon } from "../../../../assets/icons/check.svg";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import CardComponentContainer from "../../../common/cardContainers/CardComponentContainer";
import FormField from "../../../common/forms/FormField";
import AppContext from "../../../../AppContext";

// #region SCOPED CSS
const StyledPage = styled.div`
    .integration-status {
        text-align: right;

        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        letter-spacing: 0.15px;
        text-transform: uppercase;
    }

    .x {
        width: 16px;
        height: 16px;
        vertical-align: middle;
        margin-bottom: 2px;
    }
`;

const InputFieldCardStyle = styled.div`
    &.input-field-card {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
        border-radius: 16px;
        padding: 16px 24px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        position: relative;
        backdrop-filter: blur(50px);
    }
    &.disabled {
        cursor: not-allowed;
    }
    .field-label {
        color: var(--text-icons-gray-100, #d1d1d1);
        text-align: left;
        font-family: var(--subtitle-1-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--subtitle-1-font-size, 14px);
        letter-spacing: var(--subtitle-1-letter-spacing, 0.0025em);
        font-weight: var(--subtitle-1-font-weight, 400);
    }
`;
// #endregion

/**
 * IntegrationCard component for managing POS integration settings and connection status.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.integrationActive - Whether the integration is currently active.
 * @param {boolean} props.isPOSConnected - Whether the POS is connected.
 * @param {boolean} props.isConnectingAny - Whether any connection process is active.
 * @param {boolean} props.isLoadingAny - Whether any loading process is active.
 * @param {function} props.onConnectClicked - Handler for the connection/save action.
 * @param {function} props.onDisconnectClicked - Handler for the disconnect action.
 * @param {function} props.onEditClicked - Handler for the edit action.
 * @param {Array} [props.integrationFields] - Array of field objects containing name and value. // [{name: string, value: string}]
 * @param {function} [props.onSaveStateChanged] - Callback to notify parent of save button state changes. Receives { enabled: boolean, onClick: function }.
 */
export const IntegrationCard = ({
    integrationActive,
    isPOSConnected,
    isConnectingAny,
    isLoadingAny,
    onConnectClicked,
    onDisconnectClicked,
    onEditClicked,
    integrationFields,
    onSaveStateChanged
}) => {
    // #region STATES
    const { setHasUnsavedChanges } = useContext(AppContext);
    const [numOfSteps] = useState(integrationFields?.length || 0);
    const [stepsArray, setStepsArray] = useState(
        integrationFields?.map(field => ({ name: field?.name, value: field?.value }))
    );
    // #endregion

    // #region METHODS
    const isSaveEnabled = () => {
        return (
            !isConnectingAny &&
            !isLoadingAny &&
            !isPOSConnected &&
            stepsArray.length === numOfSteps &&
            numOfSteps > 0 &&
            stepsArray.every(step => step?.value?.trim()?.length > 0)
        );
    };

    const onChange = (fieldIndex, text) => {
        setHasUnsavedChanges(true);

        setStepsArray(prev => {
            const updatedStepsArray = prev.map((ss, ssIndex) => {
                if (ssIndex === fieldIndex) return { ...ss, value: text };
                else return ss;
            });
            return updatedStepsArray;
        });
    };

    const onSaveClicked = () => {
        onConnectClicked(stepsArray);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        onSaveStateChanged?.({
            enabled: isSaveEnabled(),
            onClick: onSaveClicked
        });
    }, [stepsArray, isConnectingAny, isLoadingAny, isPOSConnected]);
    // #endregion

    return (
        <StyledPage>
            <CardComponentContainer
                title="INTEGRATION"
                spacing="16px"
                rightComponent={<RightLabel isPOSConnected={isPOSConnected} isConnectingAny={isConnectingAny} />}>
                <div
                    className="flex flex-column gap-16px"
                    style={{
                        borderRadius: "10px",
                        border: "1px solid var(--backgrounds-lines-n-100, #3C485A)",
                        padding: "16px",
                        marginTop: "24px"
                    }}>
                    {integrationFields?.map((field, fieldIndex) => (
                        <InputFieldCard
                            key={fieldIndex}
                            label={field?.name}
                            disabled={!integrationActive || isLoadingAny || isConnectingAny}
                            inputField={{
                                placeholder: `Paste ${field?.name} here`,
                                invalidAssistiveText: `Invalid ${field?.name}`,
                                defaultValue: field?.value,
                                onChange: text => onChange(fieldIndex, text)
                            }}
                        />
                    ))}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "32px" }}>
                    {
                        isPOSConnected ? (
                            <>
                                <BtnLargeSolid
                                    title="DISCONNECT"
                                    onClick={onDisconnectClicked}
                                    disabled={true}
                                    normalColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    hoverColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    activeColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    disabledColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    textStyle={{ fontSize: "12px" /*, color: "var(--text-icons-red-500)" */ }}
                                    style={{ width: "150px", marginRight: "40px" }}
                                />
                                <BtnLargeSolid
                                    title="EDIT"
                                    onClick={onEditClicked}
                                    disabled={true}
                                    normalColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    hoverColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    activeColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    disabledColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                                    textStyle={{ fontSize: "12px" }}
                                    style={{ width: "150px", marginRight: "40px" }}
                                />
                            </>
                        ) : null
                        /* SAVE button moved to MainWindowHeader in POSConfig.jsx
                    ) : (
                        <BtnLargeSolid
                            title="SAVE"
                            onClick={onSaveClicked}
                            disabled={!isSaveEnabled()}
                            normalColor="var(--App-Accent-Active)"
                            hoverColor="var(--App-Accent-Hover)"
                            activeColor="var(--App-Accent-Active)"
                            disabledColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                            textStyle={{ fontSize: "12px" }}
                            style={{ width: "150px", marginRight: "40px" }}
                        />
                    )*/
                    }
                </div>
            </CardComponentContainer>
        </StyledPage>
    );
};

// #region SMALL COMPONENTS
/**
 * InputFieldCard - A simple card with a label and a text input field.
 *
 * @param {string} label - The label text displayed above the input
 * @param {boolean} disabled - Whether the input is locked
 * @param {{ placeholder: string, invalidAssistiveText: string, defaultValue: string, onChange: function }} inputField - Input field configuration
 */
const InputFieldCard = ({ label, disabled, inputField }) => {
    let inputRef = useRef(null);

    const onChangeInput = text => {
        if (inputRef?.isValid) setTimeout(() => inputRef?.isValid(true), 50);
        if (inputField?.onChange) inputField.onChange(text);
    };

    return (
        <InputFieldCardStyle className={`input-field-card ${disabled ? "disabled" : ""}`}>
            <div className="field-label">{label}</div>

            <FormField
                type="text"
                ref={el => (inputRef = el)}
                varName="inputField"
                placeholder={inputField?.placeholder}
                onTextChange={onChangeInput}
                requiredType={disabled ? "locked" : "mandatory"}
                invalidAssistiveText={inputField?.invalidAssistiveText}
                value={inputField?.defaultValue}
                styling={{ placeholder: inputField?.placeholder }}
                disableTooltip={true}
                disableAssistiveText={!inputField?.invalidAssistiveText}
            />
        </InputFieldCardStyle>
    );
};

const RightLabel = ({ isPOSConnected, isConnectingAny }) => {
    let statusStr = "";
    let statusColor = "";

    if (isPOSConnected) {
        statusStr = "Connected";
        statusColor = "var(--text-icons-green-500, #00B050)";
    } /*else if (isConnectingAny) {
        statusStr = "Connecting...";
        statusColor = "var(--text-icons-yellow-500, #FFC107)";
    }*/ else {
        statusStr = "Disconnected";
        statusColor = "var(--text-icons-red-500, #FF3636)";
    }

    return (
        <div className="integration-status">
            {statusStr === "Disconnected" && <XCloseIcon className="x" style={{ color: statusColor }} />}
            {statusStr === "Connected" && <CheckArrowIcon className="x mr-4px" style={{ color: statusColor }} />}
            <span style={{ color: statusColor }}>{statusStr}</span>
        </div>
    );
};
// #endregion
